import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { 
  Modal, 
  Box, 
  Button, 
  Typography, 
  CircularProgress 
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { updateAccount } from '../../../features/auth/action';
import api from '../../../api';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 500 },
  maxWidth: '90vw',
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
  maxHeight: '90vh',
  overflow: 'auto',
};

const ImageCropper = ({ open, onClose, imageFile, imageType, onCropComplete }) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState('preview'); // 'preview' or 'crop'
  const [crop, setCrop] = useState({
    unit: '%',
    width: imageType === 'coverImage' ? 100 : 50,
    height: imageType === 'coverImage' ? 40 : 50,
    x: 0,
    y: imageType === 'coverImage' ? 30 : 25,
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef(null);

  React.useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
        setImageLoaded(false); // Reset image loaded state
      });
      reader.readAsDataURL(imageFile);
      // Reset to preview step when new image is loaded
      setStep('preview');
    }
  }, [imageFile]);

  const onImageLoad = useCallback((e) => {
    const img = e.currentTarget;
    imgRef.current = img;
    setImageLoaded(true);
    console.log('Image loaded:', {
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
      complete: img.complete
    });
  }, []);

  const getCroppedImg = (image, crop) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!crop || !image) {
      console.error('Missing crop or image:', { crop, image });
      return null;
    }

    // Ensure image is loaded and has valid dimensions
    if (!image.naturalWidth || !image.naturalHeight) {
      console.error('Image not loaded or invalid dimensions:', image);
      return null;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const pixelRatio = window.devicePixelRatio || 1;

    canvas.width = crop.width * pixelRatio * scaleX;
    canvas.height = crop.height * pixelRatio * scaleY;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    try {
      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        crop.width * scaleX,
        crop.height * scaleY
      );
    } catch (error) {
      console.error('Error drawing image to canvas:', error);
      return null;
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error('Canvas is empty');
            reject(new Error('Canvas is empty'));
            return;
          }
          resolve(blob);
        },
        'image/jpeg',
        0.95
      );
    });
  };

  const uploadImageToS3 = async (fileToUpload, isOriginal = false) => {
    const formData = new FormData();
    const fileName = `${isOriginal ? 'original' : 'cropped'}_${imageType}_${Date.now()}.jpg`;
    
    if (isOriginal) {
      formData.append('files', fileToUpload);
    } else {
      formData.append('files', fileToUpload, fileName);
    }

    const response = await api.post('/upload', formData, {
      headers: {
        accept: 'application/json',
        'Accept-Language': 'en-US,en;q=0.8',
        'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
      },
    });

    return response.data.data;
  };

  const handleUploadOriginal = async () => {
    if (!imageFile) return;

    setLoading(true);
    try {
      // Upload original image to S3
      const uploadResult = await uploadImageToS3(imageFile, true);
      
      // Update account with new image
      const body = imageType === 'coverImage' 
        ? { coverImage: uploadResult[0]?.imgrUrl }
        : { image: uploadResult[0]?.imgrUrl };
        
      dispatch(updateAccount(body, () => {}, 'editProfilePage'));
      
      if (onCropComplete) {
        onCropComplete(uploadResult[0]?.imgrUrl);
      }
      
      handleClose();
    } catch (error) {
      console.error('Error uploading original image:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCropComplete = async () => {
    if (!completedCrop || !imgRef.current) {
      console.error('Missing crop data or image reference:', { 
        completedCrop, 
        imgRef: imgRef.current 
      });
      return;
    }

    setLoading(true);
    try {
      console.log('Starting crop with:', { 
        crop: completedCrop, 
        image: imgRef.current,
        imageLoaded: imgRef.current?.complete,
        naturalWidth: imgRef.current?.naturalWidth,
        naturalHeight: imgRef.current?.naturalHeight
      });

      const croppedImageBlob = await getCroppedImg(imgRef.current, completedCrop);
      
      if (!croppedImageBlob) {
        throw new Error('Failed to create cropped image');
      }

      // Upload cropped image to S3
      const uploadResult = await uploadImageToS3(croppedImageBlob, false);
      
      // Update account with new image
      const body = imageType === 'coverImage' 
        ? { coverImage: uploadResult[0]?.imgrUrl }
        : { image: uploadResult[0]?.imgrUrl };
        
      dispatch(updateAccount(body, () => {}, 'editProfilePage'));
      
      if (onCropComplete) {
        onCropComplete(uploadResult[0]?.imgrUrl);
      }
      
      handleClose();
    } catch (error) {
      console.error('Error cropping image:', error);
      // You could add a toast notification here to inform the user
      alert('Failed to crop image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getAspectRatio = () => {
    return imageType === 'coverImage' ? 16 / 6 : 1; // Cover image is wider, profile is square
  };

  const handleClose = () => {
    setStep('preview');
    setCompletedCrop(null);
    setImageLoaded(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <Typography variant="h6" component="h2" gutterBottom>
          {step === 'preview' 
            ? `Preview ${imageType === 'coverImage' ? 'Cover' : 'Profile'} Image`
            : `Crop Your ${imageType === 'coverImage' ? 'Cover' : 'Profile'} Image`
          }
        </Typography>
        
        {imageSrc && (
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
            {step === 'preview' ? (
              // Preview Mode - Show full image
              <Box sx={{ textAlign: 'center' }}>
                <img
                  src={imageSrc}
                  alt="Preview"
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '400px',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                />
                <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  You can upload this image as is, or crop it to fit better
                </Typography>
              </Box>
            ) : (
              // Crop Mode - Show cropping interface
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={getAspectRatio()}
                style={{ maxWidth: '100%' }}
              >
                <img
                  ref={imgRef}
                  src={imageSrc}
                  alt="Crop me"
                  onLoad={onImageLoad}
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '400px',
                    objectFit: 'contain'
                  }}
                />
              </ReactCrop>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button 
            variant="outlined" 
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          
          {step === 'preview' ? (
            // Preview Mode Buttons
            <>
              <Button 
                variant="outlined" 
                onClick={() => setStep('crop')}
                disabled={loading}
                sx={{ 
                  borderColor: '#326841',
                  color: '#326841',
                  '&:hover': { 
                    borderColor: '#275133',
                    backgroundColor: '#e8f5e8'
                  }
                }}
              >
                Crop Image
              </Button>
              <Button 
                variant="contained" 
                onClick={handleUploadOriginal}
                disabled={loading}
                sx={{ 
                  backgroundColor: '#326841',
                  '&:hover': { backgroundColor: '#275133' }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Uploading...
                  </>
                ) : (
                  'Upload as is'
                )}
              </Button>
            </>
          ) : (
            // Crop Mode Buttons
            <>
              <Button 
                variant="outlined" 
                onClick={() => setStep('preview')}
                disabled={loading}
              >
                Back to Preview
              </Button>
              <Button 
                variant="contained" 
                onClick={handleCropComplete}
                disabled={loading || !completedCrop || !imageLoaded}
                sx={{ 
                  backgroundColor: '#326841',
                  '&:hover': { backgroundColor: '#275133' }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Uploading...
                  </>
                ) : (
                  'Crop & Upload'
                )}
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ImageCropper; 